/**
 * TA Cockpit → Planning → Hiring demand.
 *
 * Collects next-trimester hiring demand from unit leads on one timeline, grouped
 * Department → Unit → Team from the org chart, with the year to date included so
 * a unit can see its own progress rather than only its backlog.
 *
 * Two layers on one board, and the distinction is load-bearing: rows from the
 * Hiring Database are read-only facts, rows entered here are drafts the unit
 * owns until somebody approves them into the HDB. demandUi.tsx explains why they
 * are drawn as differently as they are.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  addDemandTeam,
  ApiError,
  approveDemandDraft,
  deleteDemandDraft,
  fetchDemandBoard,
  linkDemandHire,
  moveDemandRoleToTeam,
  placeDemandRow,
  saveDemandDraft,
  submitDemandSection,
  submitDemandUnit,
} from '../../../services/taDemandApi';
import type {
  BoardRole,
  DemandBoard,
  DraftInput,
  HireReason,
  RosterMember,
} from '../../../types/taDemand';
import { isDraft } from '../../../types/taDemand';
import { rolesOfDept } from './demandModel';
import { DemandBanners } from './DemandBanners';
import { WindowPicker } from './WindowPicker';
import { FixMeList } from './FixMeList';
import { HireReview } from './HireReview';
import { BaselineActions } from './BaselineActions';
import { defaultWindow, type DemandWindow } from './demandWindow';
import {
  DeptFooter,
  DEPT_DOT,
  GroupRow,
  HeaderRow,
  Legend,
  SectionChip,
} from './DemandChrome';
import { SummaryStrip } from './SummaryStrip';
import { UnitBlock } from './DemandUnitBlock';
import { DataSources } from './DataSources';
import { EditedTag } from './demandUi';
import { oneLevelDown } from './demandLevels';
import { ScrollSync } from './ScrollSync';
import { RestructureToggle } from './Restructure';
import { useDemandLayout } from './useDemandLayout';
import type { Placement, PlacementKind } from '../../../types/taDemand';
import { useTeamMove } from './TeamMove';

export function HiringDemandTab({ period }: { period: string }) {
  const [board, setBoard] = useState<DemandBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  /*
    WHICH ROLES GET A ROW. Derived from the period rather than initialised from
    it, and the stored choice carries the period it was made for: a move from
    t3-2026 to t1-2027 would otherwise keep the 2026 window and filter every row
    off the new board, which reads as "no roles" rather than as a filter.
  */
  const [picked, setPicked] = useState<{ period: string; w: DemandWindow } | null>(null);
  const win = picked?.period === period ? picked.w : defaultWindow(period);
  /** Which review item is saving — the row disables itself, not the whole queue. */
  const [hireBusy, setHireBusy] = useState<string | null>(null);
  const setWin = (w: DemandWindow) => setPicked({ period, w });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [composing, setComposing] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState<string | null>(null);
  /** Why the last add-role attempt failed, shown on the composer row itself. */
  const [composeError, setComposeError] = useState<string | null>(null);

  /**
   * `refresh` never touches `loading` — it is what the mount effect and every
   * mutation use. `loading` already starts true, and setting it again inside the
   * effect would be a cascading render.
   */
  const refresh = useCallback(
    () =>
      fetchDemandBoard(period)
        .then((b) => {
          setBoard(b);
          setError(null);
        })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false)),
    [period],
  );

  /** The Retry button, which should show the spinner again. */
  const reload = useCallback(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const { tracks, tree, units, backfills, stats, template, minWidth, pastHires } =
    useDemandLayout(board, showPast, win);

  const isOpen = (key: string, dflt = false) => open[key] ?? dflt;
  const toggle = (key: string, dflt = false) =>
    setOpen((o) => ({ ...o, [key]: !(o[key] ?? dflt) }));

  /**
   * Every mutation reloads the board. It is shared between six departments, so
   * a locally-patched tree would quietly disagree with what the next person
   * opening the tab sees.
   */
  const run = useCallback(
    async (key: string, fn: () => Promise<unknown>, ok?: string): Promise<boolean> => {
      setBusy((b) => ({ ...b, [key]: true }));
      setNotice(null);
      try {
        await fn();
        await refresh();
        if (ok) setNotice(ok);
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        // A 501 is "the write token isn't configured" — a message for the
        // reader, not a failure to apologise for.
        setNotice(e instanceof ApiError && e.status === 501 ? msg : `That didn't save: ${msg}`);
        // Reported, not rethrown — but the ANSWER matters: the import control
        // keeps the file it picked until this says the server took it.
        return false;
      } finally {
        setBusy((b) => ({ ...b, [key]: false }));
      }
    },
    [refresh],
  );

  const patchDraft = useCallback(
    (role: BoardRole, patch: Partial<DraftInput>) => {
      if (!isDraft(role)) return;
      const input: DraftInput = {
        department: role.department,
        unit: role.unit,
        team: role.team,
        title: role.title,
        level: role.level,
        reason: role.reason,
        month: role.month,
        count: role.count,
        fte: role.fte,
        ...patch,
      };
      void run(role.id, () => saveDemandDraft(period, role.id, input));
    },
    [period, run],
  );

  /**
   * Place a new role. Deliberately does NOT go through `run`: a failure here has
   * to be reported on the composer row itself, because the row is often far down
   * a scrolled board and a banner at the top reads as "the click did nothing".
   * The composer also stays open on failure so the typed title is not lost.
   */
  const place = useCallback(
    async (
      department: string,
      unit: string,
      team: string,
      month: string,
      f: { title: string; level: string; reason: HireReason; count: number; fte: number },
    ) => {
      const key = `${department}|${unit}|${team}`;
      setBusy((b) => ({ ...b, [key]: true }));
      setComposeError(null);
      try {
        const id = `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
        await saveDemandDraft(period, id, { department, unit, team, month, ...f });
        await refresh();
        // Make sure the row is actually visible: the team it landed in may have
        // been collapsed, which would look exactly like nothing having happened.
        setOpen((o) => ({ ...o, [department]: true, [`${department}|${unit}`]: true, [key]: true }));
        setComposing(null);
      } catch (e) {
        setComposeError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy((b) => ({ ...b, [key]: false }));
      }
    },
    [period, refresh],
  );

  /**
   * Raise a backfill for an existing seat.
   *
   * It becomes an ordinary DRAFT — same review, same Approve button, same
   * handover to the HDB — carrying the seat it replaces. Nothing about this
   * shortcut bypasses the approval step; it only saves retyping a role that
   * already exists.
   *
   * The start month defaults to the first month still open for input, since a
   * resignation is usually urgent and the lead can drag it from there.
   */
  /**
   * Move a role into a team.
   *
   * Goes through `run` like every other mutation, so the board is re-read rather
   * than locally patched: the team is DERIVED server-side (job-family map plus
   * overrides), and a client that guessed the outcome would disagree with the
   * next person to open the tab.
   */
  const moveTeam = useCallback(
    (role: BoardRole, team: string) => {
      if ((role.team ?? '') === team) return;
      void run(role.id, () => moveDemandRoleToTeam(period, role.id, team, role.department));
    },
    [period, run],
  );
  const teamMove = useTeamMove(moveTeam);

  /**
   * Restructure mode: OFF by default, and that is the confirmation step.
   *
   * Moving a whole team between units is rare and consequential next to the
   * everyday act of adding a role; a control for it sitting permanently on every
   * team header would be the most dangerous thing on the board. There is no
   * second confirmation, because every move is a delta and removing it puts the
   * row straight back.
   */
  const [restructuring, setRestructuring] = useState(false);

  const placeRow = useCallback(
    (
      body: {
        kind: PlacementKind;
        id?: string;
        department?: string;
        unit?: string;
        team?: string;
        section?: string;
      },
      to: Placement,
    ) => {
      const key = body.kind === 'team' ? `${body.department}|${body.unit}|${body.team}` : body.id;
      void run(`place-${body.kind}-${key}`, () => placeDemandRow(period, { ...body, to }));
    },
    [period, run],
  );

  const requestBackfill = useCallback(
    (unitName: string, department: string, member: RosterMember, firstOpenMonth: string) => {
      const id = `bf${member.id}`;
      void run(member.id, () =>
        saveDemandDraft(period, id, {
          department,
          unit: unitName,
          team: member.team || '',
          title: member.position || member.name,
          // ONE STEP DOWN the ladder, by default. A backfill is very rarely
          // hired at the level of the person leaving — the seat is re-opened a
          // step down and grows back. It is a default, not a rule: the row's own
          // level picker edits it.
          level: oneLevelDown(member.level),
          reason: 'backfill',
          month: firstOpenMonth,
          count: 1,
          fte: member.fte || 1,
          employeeId: member.id,
          backfillFor: member.name,
        }),
      );
    },
    [period, run],
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading hiring demand…
      </div>
    );
  }
  if (error || !board || !stats) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-sm text-rose-700">
        <AlertCircle className="h-5 w-5" />
        <div>Could not load hiring demand: {error}</div>
        <button
          onClick={reload}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" style={{ ['--demand-tmpl' as string]: template }}>
      <DemandBanners board={board} notice={notice} />

      <SummaryStrip stats={stats} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <WindowPicker
          value={win}
          onChange={setWin}
          roles={rolesOfDept ? tree.flatMap(rolesOfDept).length : 0}
          total={board.hdb.length + board.drafts.length}
        />
        <RestructureToggle on={restructuring} onChange={setRestructuring} />
      </div>
      <BaselineActions
        period={period}
        snapshots={board.snapshots ?? []}
        busy={!!busy['freeze-baseline']}
        run={run}
        setNotice={setNotice}
      />

      {/*
        ⚠️ THE BOARD DOES NOT STRETCH TO THE VIEWPORT, and that was tried.
        Breaking it out to 94vw made the whole table fit on a wide screen — which
        sounds like the goal and is the opposite of it: with nothing overflowing
        there is nothing to scroll, so BOTH scrollbars disappeared and the
        columns sat marooned across two thousand pixels. The columns are sized
        for reading (an 800px frozen column, fifteen months); the container stays
        the cockpit's own width and the board scrolls inside it. A scrollbar is
        the point, not a failure.
      */}
      <div>
        <ScrollSync className="rounded-xl border border-gray-200 bg-white" width={minWidth}>
          <div style={{ minWidth }}>
          <HeaderRow
            tracks={tracks}
            showPast={showPast}
            onTogglePast={() => setShowPast((v) => !v)}
            pastHires={pastHires}
          />

          {tree.map((dept) => {
            const dOpen = isOpen(dept.key);
            const section = board.sections?.[dept.name];
            // The "unmapped" bucket is a diagnostic, not a unit anyone submits.
            const realUnits = dept.units.filter((u) => !u.key.endsWith('__unmapped__'));
            const unitsTotal = realUnits.length;
            const unitsSubmitted = realUnits.filter(
              (u) => board.units?.[`${dept.name}|${u.name}`]?.submittedAt,
            ).length;
            return (
              <div key={dept.key}>
                <GroupRow
                  tracks={tracks}
                  depth={0}
                  months={board.months}
                  currentMonth={board.currentMonth}
                  onClick={() => toggle(dept.key)}
                  open={dOpen}
                  tone="dept"
                  roles={rolesOfDept(dept)}
                  label={
                    <>
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: DEPT_DOT[dept.name] ?? '#9ca3af' }}
                      />
                      <span className="truncate text-[15px] font-semibold text-gray-900">
                        {dept.name}
                      </span>
                      <SectionChip
                        section={section}
                        unitsSubmitted={unitsSubmitted}
                        unitsTotal={unitsTotal}
                      />
                      <EditedTag by={section?.lastEditBy} at={section?.lastEditAt} />
                    </>
                  }
                />
                {dOpen &&
                  dept.units.map((unit) => (
                    <UnitBlock
                      key={unit.key}
                      unit={unit}
                      tracks={tracks}
                      months={board.months}
                      currentMonth={board.currentMonth}
                      canApprove={board.canApprove}
                      isOpen={isOpen}
                      toggle={toggle}
                      composing={composing}
                      setComposing={setComposing}
                      busy={busy}
                      onPatch={patchDraft}
                      onPlace={(unitName, team, month, f) => {
                        void place(dept.name, unitName, team, month, f);
                      }}
                      composeError={composeError}
                      onApprove={(r) =>
                        void run(r.id, () =>
                          approveDemandDraft(period, r.id).then((res) => {
                            if (res.warning) setNotice(res.warning);
                          }),
                        )
                      }
                      onDelete={(r) =>
                        void run(r.id, () => deleteDemandDraft(period, r.id, r.department))
                      }
                      onAddTeam={(name) =>
                        void run(unit.key, () =>
                          addDemandTeam(period, dept.name, unit.name, name),
                        )
                      }
                      unitMeta={board.units}
                      onSubmitUnit={(unitName) =>
                        void run(
                          `unit-submit-${dept.name}|${unitName}`,
                          () => submitDemandUnit(period, dept.name, unitName),
                          `${unitName} submitted. Nothing is locked — it stays editable.`,
                        )
                      }
                      deptName={dept.name}
                      teamMove={teamMove}
                      restructuring={restructuring}
                      unitChoices={units}
                      onRestructure={placeRow}
                      backfills={backfills}
                      onRequestBackfill={(unitName, member) =>
                        requestBackfill(
                          unitName,
                          dept.name,
                          member,
                          board.months.find((m) => !m.past)?.key ?? board.currentMonth,
                        )
                      }
                    />
                  ))}
                {dOpen && dept.key !== '__unassigned__' && (
                  <DeptFooter
                    dept={dept}
                    section={section}
                    unitsSubmitted={unitsSubmitted}
                    unitsTotal={unitsTotal}
                    busy={!!busy[`submit-${dept.name}`]}
                    onSubmit={() =>
                      void run(
                        `submit-${dept.name}`,
                        () => submitDemandSection(period, dept.name),
                        `${dept.name} submitted — the board stays open for changes.`,
                      )
                    }
                  />
                )}
              </div>
            );
          })}
          </div>
        </ScrollSync>
      </div>

      <Legend canApprove={board.canApprove} />

      {/*
        EVERYTHING ABOUT THE DATA SITS BELOW THE BOARD (Theresa, Sep 15 2026).
        These three say where the numbers came from; above the table they made a
        lead scroll past provenance to reach the plan. The baseline stays above
        because freezing is an action on the round, not a note about it — and so
        do the degradation banners, which are not notes about the data but the
        reason the board is incomplete: "the Hiring Database could not be read"
        has to be seen before the empty table it explains, not after.
      */}
      <HireReview
        items={board.hireReview ?? []}
        roster={board.roster ?? []}
        links={board.hireLinks ?? {}}
        busyId={hireBusy}
        onDecide={(roleId, personId) => {
          setHireBusy(roleId);
          void run(`hire-link-${roleId}`, () => linkDemandHire(period, roleId, personId)).finally(() =>
            setHireBusy(null),
          );
        }}
      />
      <FixMeList items={board.hireFixups ?? []} />
      {board.diagnostics && <DataSources d={board.diagnostics} />}
    </div>
  );
}
